import type { DocumentFieldState, StructuredDocumentRecord, StructuredLineItemRecord } from '@/features/case-workspace/types'
import {
  type HtsRegulatoryFlag,
  type NormalizedHtsRecord,
  usitcHtsLookupService,
} from '@/features/case-workspace/usitcHts'

function parseNumericPrefix(value: string) {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)/)
  return match?.[1] ?? ''
}

function parseQuantityUnit(value: string) {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s+(.+)$/)
  if (!match) return null
  return {
    quantity: Number(match[1]),
    unit: match[2].trim().toLowerCase(),
  }
}

function normalizeRateUnit(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/s$/, '')
}

function calculateRateAmount(rate: string, enteredValue: number, quantityWithUnit: ReturnType<typeof parseQuantityUnit>) {
  const trimmedRate = rate.trim()
  if (!trimmedRate) return 0
  if (/^free\b/i.test(trimmedRate)) return 0

  const percentMatch = trimmedRate.match(/(\d+(?:\.\d+)?)%/)
  if (percentMatch) {
    return enteredValue * (Number(percentMatch[1]) / 100)
  }

  const centsPerUnitMatch = trimmedRate.match(/(\d+(?:\.\d+)?)¢\/([a-z]+)/i)
  if (centsPerUnitMatch && quantityWithUnit) {
    const [, amount, unit] = centsPerUnitMatch
    if (normalizeRateUnit(quantityWithUnit.unit).includes(normalizeRateUnit(unit))) {
      return quantityWithUnit.quantity * (Number(amount) / 100)
    }
  }

  const dollarsPerUnitMatch = trimmedRate.match(/\$?(\d+(?:\.\d+)?)\/([a-z]+)/i)
  if (dollarsPerUnitMatch && quantityWithUnit) {
    const [, amount, unit] = dollarsPerUnitMatch
    if (normalizeRateUnit(quantityWithUnit.unit).includes(normalizeRateUnit(unit))) {
      return quantityWithUnit.quantity * Number(amount)
    }
  }

  return 0
}

function formatCurrency(amount: number) {
  return amount.toFixed(2)
}

function parseCurrency(value: string | undefined) {
  if (!value) return 0
  const normalized = value.replace(/[^0-9.-]/g, '')
  const numeric = Number(normalized)
  return Number.isFinite(numeric) ? numeric : 0
}

function formatDateForForm(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear())
  return `${month}/${day}/${year}`
}

function uniqueFlags(flags: HtsRegulatoryFlag[]) {
  return [...new Set(flags)]
}

function netQuantityUnitLabel(lookupRecord: NormalizedHtsRecord | null) {
  return lookupRecord?.units?.[0] ?? 'units'
}

function buildCompositeMerchandiseDescription(
  htsDescription: string,
  existingDescription: string,
  origin: DocumentFieldState['origin'] | undefined,
) {
  const normalizedHtsDescription = htsDescription.trim()
  const normalizedExistingDescription = existingDescription.trim()

  if (!normalizedHtsDescription) return normalizedExistingDescription
  if (!normalizedExistingDescription) return normalizedHtsDescription
  if (
    normalizedExistingDescription === normalizedHtsDescription
    || normalizedExistingDescription.startsWith(`${normalizedHtsDescription};`)
  ) {
    return normalizedExistingDescription
  }

  if (origin === 'system') {
    const existingSuffix = normalizedExistingDescription.includes(';')
      ? normalizedExistingDescription.split(';').slice(1).join(';').trim()
      : ''
    return existingSuffix ? `${normalizedHtsDescription}; ${existingSuffix}` : normalizedHtsDescription
  }

  return `${normalizedHtsDescription}; ${normalizedExistingDescription}`
}

function autoAdCvdRate(lookupRecord: NormalizedHtsRecord | null, flags: HtsRegulatoryFlag[]) {
  if (flags.includes('AD_CVD_POSSIBLE')) return 'Review required'
  return ''
}

function autoIrcRate(lookupRecord: NormalizedHtsRecord | null, flags: HtsRegulatoryFlag[]) {
  if (!flags.includes('IRC_TAX_APPLICABLE')) return 'Not Applicable to HTSUS code'

  const explicitRateCandidates = [
    lookupRecord?.additionalDuties?.trim(),
  ].filter(Boolean) as string[]

  const explicitRate = explicitRateCandidates.find((candidate) => {
    const normalized = candidate.trim().toLowerCase()
    return normalized !== 'no additional duty'
  })

  return explicitRate || 'Not Applicable to HTSUS code'
}

export function deriveCbp7501LineAutomation(lineItem: StructuredLineItemRecord) {
  const htsusNumber = lineItem.fields.htsus_number?.value?.trim() ?? ''
  const lookupRecord = usitcHtsLookupService.lookup(htsusNumber)
  const flags = uniqueFlags(lookupRecord?.flags ?? [])
  const enteredValue = lineItem.fields.entered_value?.value?.trim() || '0.00'
  const charges = lineItem.fields.charges?.value?.trim() || '0.00'
  const relationship = lineItem.fields.relationship?.value?.trim() || 'N'
  const htsusRate = lookupRecord?.generalRate?.trim() || ''
  const adCvdRate = autoAdCvdRate(lookupRecord, flags)
  const ircRate = autoIrcRate(lookupRecord, flags)
  const description = lineItem.fields.description_of_merchandise?.value?.trim() || lookupRecord?.description || ''
  const quantityValue = parseNumericPrefix(lineItem.fields.net_quantity_htsus_units?.value ?? '')
  const netUnit = netQuantityUnitLabel(lookupRecord)
  const quantityWithUnit = parseQuantityUnit(lineItem.fields.net_quantity_htsus_units?.value ?? '')
  const enteredValueAmount = Number(enteredValue || 0)
  const htsusDutyAmount = calculateRateAmount(htsusRate, enteredValueAmount, quantityWithUnit)
  const adCvdDutyAmount = calculateRateAmount(adCvdRate, enteredValueAmount, quantityWithUnit)
  const ircTaxAmount = calculateRateAmount(ircRate, enteredValueAmount, quantityWithUnit)
  const otherFeesAmount = parseCurrency(charges)
  const calculatedDutyAndIrTax = formatCurrency(
    htsusDutyAmount + adCvdDutyAmount + ircTaxAmount,
  )
  const dutyAndIrTax = calculatedDutyAndIrTax

  return {
    lookupRecord,
    flags,
    description,
    enteredValue,
    charges,
    relationship,
    htsusRate,
    adCvdRate,
    ircRate,
    htsusDutyAmount,
    adCvdDutyAmount,
    ircTaxAmount,
    otherFeesAmount,
    dutyAndIrTax,
    netQuantityValue: quantityValue,
    netQuantityUnit: netUnit,
    quotaOrVisaMessage: flags.includes('QUOTA_OR_VISA') ? 'Review quota or visa requirement' : '',
  }
}

export function buildNetQuantityValue(quantityValue: string, lookupRecord: NormalizedHtsRecord | null) {
  const trimmed = quantityValue.trim()
  if (!trimmed) return ''
  return `${trimmed} ${netQuantityUnitLabel(lookupRecord)}`
}

export function applyCbp7501LineAutomation(lineItem: StructuredLineItemRecord, actorLabel: string) {
  const automation = deriveCbp7501LineAutomation(lineItem)
  const now = new Date().toISOString()
  const existingDescription = lineItem.fields.description_of_merchandise?.value ?? ''
  const nextDescription = buildCompositeMerchandiseDescription(
    automation.lookupRecord?.description ?? '',
    existingDescription,
    lineItem.fields.description_of_merchandise?.origin,
  )

  return {
    ...lineItem,
    fields: {
      ...lineItem.fields,
      description_of_merchandise: {
        ...lineItem.fields.description_of_merchandise,
        value: nextDescription,
        displayValue: nextDescription,
        origin:
          nextDescription
            ? 'system'
            : (lineItem.fields.description_of_merchandise?.origin ?? 'manual'),
        reviewedValue: nextDescription || undefined,
        lastUpdatedAt: now,
        lastUpdatedBy: actorLabel,
      },
      htsus_rate: {
        ...lineItem.fields.htsus_rate,
        value: automation.htsusRate,
        displayValue: automation.htsusRate,
        origin: 'system',
        reviewedValue: automation.htsusRate,
        lastUpdatedAt: now,
        lastUpdatedBy: actorLabel,
      },
      ad_cvd_rate: {
        ...setSystemFieldValue(lineItem.fields.ad_cvd_rate, automation.adCvdRate, now, actorLabel),
      },
      irc_rate: {
        ...lineItem.fields.irc_rate,
        value: automation.ircRate,
        displayValue: automation.ircRate,
        origin: automation.ircRate ? 'system' : (lineItem.fields.irc_rate?.origin ?? 'manual'),
        reviewedValue: automation.ircRate || undefined,
        lastUpdatedAt: now,
        lastUpdatedBy: actorLabel,
      },
      duty_and_ir_tax: {
        ...lineItem.fields.duty_and_ir_tax,
        value: automation.dutyAndIrTax,
        displayValue: automation.dutyAndIrTax,
        origin: 'system',
        reviewedValue: automation.dutyAndIrTax,
        lastUpdatedAt: now,
        lastUpdatedBy: actorLabel,
      },
    },
  }
}

function shouldPreserveManualValue(fieldState: DocumentFieldState | undefined) {
  return Boolean(fieldState?.value?.trim()) && fieldState?.origin !== 'system'
}

function setSystemFieldValue(
  fieldState: DocumentFieldState | undefined,
  nextValue: string,
  now: string,
  actorLabel: string,
) {
  if (!fieldState) {
    return {
      value: nextValue,
      displayValue: nextValue,
      origin: 'system' as const,
      reviewedValue: nextValue || undefined,
      lastUpdatedAt: now,
      lastUpdatedBy: actorLabel,
    }
  }

  if (shouldPreserveManualValue(fieldState)) {
    return fieldState
  }

  return {
    ...fieldState,
    value: nextValue,
    displayValue: nextValue,
    origin: nextValue ? 'system' : (fieldState.origin ?? 'manual'),
    reviewedValue: nextValue || undefined,
    lastUpdatedAt: now,
    lastUpdatedBy: actorLabel,
  }
}

function buildBrokerFilerInformation(record: StructuredDocumentRecord, actorLabel: string) {
  const parts = [
    actorLabel.trim() || '',
    record.fields.entry_number?.value?.trim()
      ? `Filer code: ${record.fields.entry_number.value.trim().slice(0, 3)}`
      : '',
    record.fields.importer_number?.value?.trim()
      ? `Importer no.: ${record.fields.importer_number.value.trim()}`
      : '',
  ].filter(Boolean)

  return parts.join('\n')
}

function buildBrokerImporterFileNumber(record: StructuredDocumentRecord) {
  return (
    record.fields.reference_number?.value?.trim()
    || record.fields.entry_number?.value?.trim()
    || record.caseId
  )
}

function deriveDeclarantTitle(record: StructuredDocumentRecord) {
  const role = record.fields.declaration_role?.value?.trim()
  switch (role) {
    case 'importer_of_record':
      return 'Importer of Record'
    case 'owner':
      return 'Owner'
    case 'purchaser':
      return 'Purchaser'
    case 'authorized_agent':
      return 'Authorized Agent'
    default:
      return ''
  }
}

export function deriveCbp7501DocumentAutomation(record: StructuredDocumentRecord, actorLabel: string) {
  const lineAutomations = record.lineItems.map((lineItem) => deriveCbp7501LineAutomation(lineItem))
  const totalEnteredValue = lineAutomations.reduce(
    (total, automation) => total + parseCurrency(automation.enteredValue),
    0,
  )
  const totalDuty = lineAutomations.reduce(
    (total, automation) => total + automation.htsusDutyAmount + automation.adCvdDutyAmount,
    0,
  )
  const totalTax = lineAutomations.reduce(
    (total, automation) => total + automation.ircTaxAmount,
    0,
  )
  const totalOtherFees = lineAutomations.reduce(
    (total, automation) => total + automation.otherFeesAmount,
    0,
  )
  const grandTotal = totalDuty + totalTax + totalOtherFees

  return {
    totalEnteredValue: formatCurrency(totalEnteredValue),
    dutyTotal: formatCurrency(totalDuty),
    taxTotal: formatCurrency(totalTax),
    otherTotal: formatCurrency(totalOtherFees),
    totalOtherFees: formatCurrency(totalOtherFees),
    grandTotal: formatCurrency(grandTotal),
    declarantName: actorLabel.trim(),
    declarantTitle: deriveDeclarantTitle(record),
    signatureDate: formatDateForForm(new Date()),
    brokerFilerInformation: buildBrokerFilerInformation(record, actorLabel),
    brokerImporterFileNumber: buildBrokerImporterFileNumber(record),
  }
}

export function applyCbp7501DocumentAutomation(record: StructuredDocumentRecord, actorLabel: string) {
  const now = new Date().toISOString()
  const automation = deriveCbp7501DocumentAutomation(record, actorLabel)

  return {
    ...record,
    fields: {
      ...record.fields,
      total_entered_value: setSystemFieldValue(record.fields.total_entered_value, automation.totalEnteredValue, now, actorLabel),
      duty_total: setSystemFieldValue(record.fields.duty_total, automation.dutyTotal, now, actorLabel),
      tax_total: setSystemFieldValue(record.fields.tax_total, automation.taxTotal, now, actorLabel),
      total_other_fees: setSystemFieldValue(record.fields.total_other_fees, automation.totalOtherFees, now, actorLabel),
      other_total: setSystemFieldValue(record.fields.other_total, automation.otherTotal, now, actorLabel),
      grand_total: setSystemFieldValue(record.fields.grand_total, automation.grandTotal, now, actorLabel),
      declarant_name: setSystemFieldValue(record.fields.declarant_name, automation.declarantName, now, actorLabel),
      declarant_title: setSystemFieldValue(record.fields.declarant_title, automation.declarantTitle, now, actorLabel),
      signature_date: setSystemFieldValue(record.fields.signature_date, automation.signatureDate, now, actorLabel),
      broker_filer_information: setSystemFieldValue(
        record.fields.broker_filer_information,
        automation.brokerFilerInformation,
        now,
        actorLabel,
      ),
      broker_importer_file_number: setSystemFieldValue(
        record.fields.broker_importer_file_number,
        automation.brokerImporterFileNumber,
        now,
        actorLabel,
      ),
    },
  }
}
